import React, { useState, useMemo } from "react";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView } from "@codemirror/view";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import AdminLayout from "../../Layout/index.jsx";
import { useGetAssignmentsForStudentQuery } from "../../../../graphql/generated.tsx";
import { toast } from "../../../../toast";
import Pagination from "../../../../components/Pagination/index.jsx";

// -------------------------------------------------------------
// ✅ GraphQL
// -------------------------------------------------------------

const GET_ASSIGNMENT_SUBMIT_INLINE = gql`
  query GetAssignmentSubmitInline(
    $assignmentId: String!
    $studentId: String!
    $campus_name: String!
  ) {
    getAssignmentSubmit(
      assignmentId: $assignmentId
      studentId: $studentId
      campus_name: $campus_name
    ) {
      id
      totalScore
      completed
      endTime
      cheat_count
      cheat_reasons
      tabSwitches
      questions {
        question_id
        answer
        score
        language
      }
    }
  }
`;

const GET_ASSIGNMENT_QUESTIONS = gql`
  query GetAllAssignmentQuestionInline($assignmentId: String!) {
    getAllAssignmentQuestion(assignmentId: $assignmentId) {
      id
      title
      text
      question_sub_text
      test_cases {
        input
        output
        visibility
      }
    }
  }
`;

const REVOKE_ASSIGNMENT = gql`
  mutation RevokeAssignment(
    $assignmentId: String!
    $studentIds: [String!]!
    $campus_name: String!
  ) {
    revokeAssignment(
      assignmentId: $assignmentId
      studentIds: $studentIds
      campus_name: $campus_name
    ) {
      Status
      revokedCount
    }
  }
`;

const SubmissionCodeViewer = ({ code, language }) => {
  const [languageExtension, setLanguageExtension] = useState([]);
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);

  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let active = true;
    const loadLanguage = async () => {
      const normalizedLanguage = String(language || "").toUpperCase();
      try {
        let extension = null;
        if (normalizedLanguage === "CPP" || normalizedLanguage === "C" || normalizedLanguage === "C++") {
          const { cpp } = await import("@codemirror/lang-cpp");
          extension = cpp();
        } else if (normalizedLanguage === "PYTHON" || normalizedLanguage === "PY") {
          const { python } = await import("@codemirror/lang-python");
          extension = python();
        } else if (normalizedLanguage === "JAVA") {
          const { java } = await import("@codemirror/lang-java");
          extension = java();
        } else if (normalizedLanguage === "JAVASCRIPT" || normalizedLanguage === "JS") {
          const { javascript } = await import("@codemirror/lang-javascript");
          extension = javascript();
        }
        if (active) setLanguageExtension(extension ? [extension] : []);
      } catch {
        if (active) setLanguageExtension([]);
      }
    };

    loadLanguage();
    return () => { active = false; };
  }, [language]);

  const sourceCode = code || "// No answer submitted";
  const codeLineCount = sourceCode.split("\n").length;
  const editorHeight = Math.min(
    Math.max(codeLineCount * 24 + 24, 160),
    Math.max(viewportHeight - 220, 240)
  );
  const lightEditorTheme = EditorView.theme({
    "&": { fontSize: "16px" },
    ".cm-content": { padding: "12px 0" },
    ".cm-gutters": { fontSize: "14px" },
  });

  return (
    <CodeMirror
      value={sourceCode}
      extensions={[...languageExtension, lightEditorTheme]}
      theme="light"
      editable={false}
      readOnly
      height={`${editorHeight}px`}
      basicSetup={{ lineNumbers: true, foldGutter: true, highlightActiveLine: false }}
      className="submission-code-editor"
    />
  );
};

// -------------------------------------------------------------
// ✅ Component
// -------------------------------------------------------------

const AssignmentsForStudent = () => {
  const location = useLocation();
  const { studentId, email, campusName } = location.state || {};

  const { data, loading, error } = useGetAssignmentsForStudentQuery({
    variables: { email },
    skip: !email,
  });

  const userData = JSON.parse(localStorage.getItem("user"));
  const userDisplayName = userData?.displayName || "Admin";

  const [loadSubmission, { data: submission, loading: submissionLoading }] =
    useLazyQuery(GET_ASSIGNMENT_SUBMIT_INLINE);

  const [loadQuestions, { data: questionsData }] =
    useLazyQuery(GET_ASSIGNMENT_QUESTIONS);

  const questionMap = useMemo(() => {
    const map = {};
    (questionsData?.getAllAssignmentQuestion || []).forEach((q) => {
      map[q.id] = q;
    });
    return map;
  }, [questionsData]);

  const [revokeAssignment] = useMutation(REVOKE_ASSIGNMENT);

  const [openedRow, setOpenedRow] = useState(null);
  const [openedAssignmentName, setOpenedAssignmentName] = useState("");
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [showQuestion, setShowQuestion] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const PAGE_SIZE = 10;

  // -------------------------------------------------------------
  // ✅ Prepare dataset
  // -------------------------------------------------------------

  let pending = [];
  let completed = [];

  if (!loading && !error) {
    const result = data?.getAssignmentsForStudent || {};
    pending = result.pendingAssignments || [];
    completed = result.completedAssignments || [];
  }

  const all = useMemo(
    () => [
      ...pending.map((i) => ({ ...i, status: "Pending" })),
      ...completed.map((i) => ({ ...i, status: "Completed" })),
    ],
    [pending, completed]
  );

  // -------------------------------------------------------------
  // ✅ Filtering + Search + Sort
  // -------------------------------------------------------------

  const processedData = useMemo(() => {
    let list = [...all];

    if (filter !== "all") {
      list = list.filter((i) => i.status.toLowerCase() === filter);
    }

    if (search.trim()) {
      list = list.filter((i) =>
        i.assignment.name.toLowerCase().includes(search.toLowerCase())
      );
    }

    // ✅ Sort latest → oldest
    list.sort((a, b) => {
      const aDate =
        a.status === "Pending"
          ? new Date(a.assignment.due_date)
          : new Date(a.endTime);
      const bDate =
        b.status === "Pending"
          ? new Date(b.assignment.due_date)
          : new Date(b.endTime);
      return bDate - aDate;
    });

    return list;
  }, [all, filter, search]);


  const totalPages = Math.ceil(processedData.length / PAGE_SIZE);
  const paginatedData = processedData.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const handleViewSubmission = (assignmentId, assignmentName) => {
    setOpenedRow(assignmentId);
    setOpenedAssignmentName(assignmentName || "");
    setCurrentQIndex(0);
    setShowQuestion(false);

    loadSubmission({
      variables: {
        assignmentId,
        studentId,
        campus_name: campusName,
      },
    });

    loadQuestions({
      variables: { assignmentId },
    });
  };

  const handleCloseModal = () => {
    setOpenedRow(null);
    setOpenedAssignmentName("");
    setCurrentQIndex(0);
    setShowQuestion(false);
  };

  const handleRevoke = async (assignmentId) => {
    try {
      const { data } = await revokeAssignment({
        variables: { assignmentId, studentIds: [studentId], campus_name: campusName },
      });

      toast.success(`Assignment revoked successfully! (${data.revokeAssignment.revokedCount ?? 1} revoked)`);
    } catch (err) {
      toast.error("Error revoking assignment: " + err.message);
    }
  };

  if (loading)
    return <AdminLayout><div className="p-10 text-lg"></div></AdminLayout>;
  if (error)
    return (
      <AdminLayout>
        <div className="p-10 text-lg text-red-600">{error.message}</div>
      </AdminLayout>
    );


  return (
    <AdminLayout>
      <div className="p-4 sm:p-8">
        <div className="mb-10">
          <p className="text-xl sm:text-3xl font-bold text-gray-900">
            Showing assignments for{" "}
            <span className="font-semibold text-[#0F537E]">
              {userDisplayName}
            </span>
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">

          {/* ✅ Filter chips */}
          <div className="flex gap-3">
            {["all", "pending", "completed"].map((f) => (
              <button
                key={f}
                onClick={() => {
                  setFilter(f);
                  setPage(1);
                }}
                className={`px-4 py-2 rounded-full text-sm border transition-all duration-200
                  ${
                    filter === f
                      ? "bg-[#0F537E] text-white shadow"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`}
              >
                {f.toUpperCase()}
              </button>
            ))}
          </div>

          {/* ✅ Search Bar */}
          <input
            type="text"
            placeholder="Search assignment name..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full sm:w-72 px-4 py-2 border rounded-lg text-sm
              focus:ring-2 focus:ring-[#0F537E] focus:border-[#0F537E]"
          />
        </div>

        <div className="overflow-x-auto bg-white border shadow rounded-xl">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-100 border-b">
                {["Name", "Date", "Status", "Revoke", "View"].map(
                  (h) => (
                    <th
                      key={h}
                      className={`px-6 py-4 text-sm font-semibold text-gray-700 ${
                        h === "Revoke" || h === "View" ? "text-center" : "text-left"
                      }`}
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 && (
                <tr>
                  <td className="py-12 text-center text-gray-500" colSpan={6}>
                    No assignments found.
                  </td>
                </tr>
              )}

              {paginatedData.map((item) => {
                const a = item.assignment;

                return (
                  <React.Fragment key={a.id}>
                    <tr className={`transition border-b ${
                        openedRow === a.id
                          ? "bg-blue-50 border-l-4 border-l-[#0F537E]"
                          : "hover:bg-gray-50"
                      }`}>
                      <td className="px-6 py-4 font-medium">{a.name}</td>

                      <td className="px-6 py-4 text-gray-500">
                        {item.status === "Pending"
                          ? new Date(a.due_date).toLocaleDateString()
                          : new Date(item.endTime).toLocaleDateString()}
                      </td>

                      <td className="px-6 py-4">
                        <span
                            className={`text-sm font-semibold
                              ${
                                item.status === "Pending"
                                  ? "text-yellow-600"
                                  : "text-green-600"
                              }`}
                          >
                            {item.status}
                          </span>
                      </td>

                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleRevoke(a.id)}
                          className="px-3 py-1 rounded-md text-sm font-medium text-[#0F537E]
                          hover:bg-blue-50 transition"
                        >
                          Revoke
                        </button>
                      </td>

                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleViewSubmission(a.id, a.name)}
                          className="px-3 py-1 rounded-md text-sm font-medium text-[#0F537E]
                          hover:bg-blue-50 transition"
                        >
                          View
                        </button>
                      </td>
                    </tr>


                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      {/* ✅ SUBMISSION MODAL */}
      {openedRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-[2px]"
          onClick={handleCloseModal}
        >
          <div
            className="relative bg-white shadow-2xl w-full h-full flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#0F537E] to-[#1a6a99] px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 shrink-0">
              <h3 className="text-lg font-semibold text-white truncate max-w-xs" title={openedAssignmentName}>
                {openedAssignmentName.length > 30
                  ? openedAssignmentName.slice(0, 30) + "…"
                  : openedAssignmentName || "Submission Details"}
              </h3>
              <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 flex-wrap">
                {!submissionLoading && submission?.getAssignmentSubmit && (
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-sm text-white/90">
                    {submission.getAssignmentSubmit.endTime && (
                      <span className="hidden sm:inline">
                        Submitted:{" "}
                        {new Date(submission.getAssignmentSubmit.endTime).toLocaleString("en-US", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </span>
                    )}
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        submission.getAssignmentSubmit.completed
                          ? "bg-green-400/30 text-green-100"
                          : "bg-yellow-400/30 text-yellow-100"
                      }`}
                    >
                      {submission.getAssignmentSubmit.completed ? "Completed" : "Incomplete"}
                    </span>
                    <span className="font-semibold">
                      Score: {submission.getAssignmentSubmit.totalScore ?? 0}
                    </span>
                  </div>
                )}
                <button
                  onClick={handleCloseModal}
                  className="ml-2 text-white/80 hover:text-white transition text-xl leading-none"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-5">
              {submissionLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F537E]"></div>
                  <p className="ml-3 text-[#0F537E] font-medium">Loading submission details...</p>
                </div>
              ) : submission?.getAssignmentSubmit ? (
                <>
                  {/* Single Question View */}
                  {submission.getAssignmentSubmit.questions?.length > 0 ? (
                    (() => {
                      const questions = submission.getAssignmentSubmit.questions;
                      const q = questions[currentQIndex];
                      return (
                        <div className="overflow-hidden border border-gray-200 rounded-xl">
                          {/* Question header */}
                          <div className="flex items-center justify-between px-4 py-3 bg-gray-100 border-b border-gray-200">
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                              </svg>
                              <span className="text-sm font-semibold text-gray-700">
                                Q{currentQIndex + 1}
                                {questionMap[q.question_id]?.title
                                  ? ` — ${questionMap[q.question_id].title}`
                                  : ""}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => setShowQuestion((v) => !v)}
                                className="px-3 py-1 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
                              >
                                {showQuestion ? "View Code" : "View Question"}
                              </button>
                              <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-mono">
                                {q.language || "Unknown"}
                              </span>
                              <span className="text-xs font-semibold text-green-700">
                                Score: {q.score}
                              </span>
                            </div>
                          </div>
                          {/* Code block */}
                          {showQuestion ? (
                            <div className="p-5 bg-gray-50">
                              {questionMap[q.question_id]?.title ? (
                                <div className="mb-3 text-base font-semibold text-gray-900">
                                  {questionMap[q.question_id].title}
                                </div>
                              ) : null}
                              {questionMap[q.question_id]?.question_sub_text ? (
                                <pre className="mb-4 overflow-x-auto text-sm text-gray-700 whitespace-pre-wrap break-words">
                                  {questionMap[q.question_id].question_sub_text}
                                </pre>
                              ) : null}
                              {questionMap[q.question_id]?.text ? (
                                <div
                                  className="text-sm leading-relaxed text-gray-900"
                                  dangerouslySetInnerHTML={{
                                    __html: questionMap[q.question_id].text,
                                  }}
                                />
                              ) : (
                                <div className="text-sm text-gray-600">
                                  Question details not available.
                                </div>
                              )}

                            </div>
                          ) : (
                            <div className="bg-white">
                              <SubmissionCodeViewer code={q.answer?.trim()} language={q.language} />
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="py-6 text-center text-gray-500 text-sm">
                      No final answers submitted for this assignment.
                    </div>
                  )}
                </>
              ) : (
                <div className="py-12 text-center">
                  <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="font-medium text-gray-500">No submission found.</p>
                </div>
              )}
            </div>

            {/* Modal Footer — Prev / Next */}
            {!submissionLoading &&
              submission?.getAssignmentSubmit?.questions?.length > 0 && (
                <div className="shrink-0 flex items-center justify-between px-6 py-4 border-t bg-gray-50">
                  <button
                    disabled={currentQIndex === 0}
                    onClick={() => setCurrentQIndex((i) => i - 1)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${
                      currentQIndex === 0
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    ← Prev
                  </button>

                  <span className="text-sm text-gray-500 font-medium">
                    {currentQIndex + 1} / {submission.getAssignmentSubmit.questions.length}
                  </span>

                  <button
                    disabled={
                      currentQIndex ===
                      submission.getAssignmentSubmit.questions.length - 1
                    }
                    onClick={() => setCurrentQIndex((i) => i + 1)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${
                      currentQIndex ===
                      submission.getAssignmentSubmit.questions.length - 1
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    Next →
                  </button>
                </div>
              )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AssignmentsForStudent;
